<script lang="ts" module>
  import { type VariantProps, tv } from 'tailwind-variants';
  import type { WithElementRef } from 'bits-ui';
  import type { HTMLAnchorAttributes, HTMLButtonAttributes } from 'svelte/elements';

  export const buttonVariants = tv({
    base: "focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium tracking-[-0.01em] ring-offset-background transition-all duration-200 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-1 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    variants: {
      variant: {
        // Luxury: gradient sutil + glow primary tinted. Hover = glow más fuerte.
        default:
          'bg-gradient-to-b from-primary to-primary/85 text-primary-foreground lifted-edge shadow-[0_2px_8px_-1px_hsl(var(--primary)/0.35)] hover:shadow-[0_4px_16px_-2px_hsl(var(--primary)/0.5)] hover:from-primary hover:to-primary/95',
        destructive:
          'bg-gradient-to-b from-destructive to-destructive/85 text-destructive-foreground lifted-edge shadow-[0_2px_8px_-1px_hsl(var(--destructive)/0.35)] hover:shadow-[0_4px_16px_-2px_hsl(var(--destructive)/0.5)]',
        outline:
          'border border-border bg-surface-1 text-foreground hover:bg-surface-2 hover:border-border-strong',
        secondary:
          'bg-surface-2 text-foreground lifted-edge hover:bg-surface-3',
        ghost: 'hover:bg-surface-2 hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2 min-w-11', // 40px luxury — más respirado que 36px
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-md px-8 text-base',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  });

  export type ButtonVariant = VariantProps<typeof buttonVariants>['variant'];
  export type ButtonSize = VariantProps<typeof buttonVariants>['size'];

  export type ButtonProps = WithElementRef<HTMLButtonAttributes> &
    WithElementRef<HTMLAnchorAttributes> & {
      variant?: ButtonVariant;
      size?: ButtonSize;
    };
</script>

<script lang="ts">
  import { cn } from '$lib/utils';

  let {
    class: className,
    variant = 'default',
    size = 'default',
    ref = $bindable(null),
    href = undefined,
    type = 'button',
    children,
    ...restProps
  }: ButtonProps = $props();
</script>

{#if href}
  <a
    bind:this={ref}
    class={cn(buttonVariants({ variant, size }), className)}
    {href}
    {...restProps}
  >
    {@render children?.()}
  </a>
{:else}
  <button
    bind:this={ref}
    class={cn(buttonVariants({ variant, size }), className)}
    {type}
    {...restProps}
  >
    {@render children?.()}
  </button>
{/if}
